import React, { useState, useMemo, useEffect } from "react";
import { Drawer, Button, Checkbox, Space, Tag, Typography, Empty, Tooltip, Input } from "antd";
import { ContrastTag } from "../shared";
import {
	BellOutlined,
	ReloadOutlined,
	CheckOutlined,
	DeleteOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	CloseCircleOutlined,
	DeleteFilled,
	SearchOutlined,
	DownOutlined,
	UpOutlined,
	MessageOutlined,
} from "@ant-design/icons";
import { useNotifications } from "../../context/NotificationContext";
import { CommunicationNotification as Notification } from "../../services/CommunicationsService";
import { isQueueAlert } from "../../utils/notifications/queueAlert";

const { Text, Title } = Typography;

interface CommunicationsModalProps {
	open: boolean;
	onClose: () => void;
}

const CommunicationsModal: React.FC<CommunicationsModalProps> = ({ open, onClose }) => {
	const { notifications, unreadCount, isLoading, markAsRead, deleteNotification, bulkDelete, deleteAll, markAllAsRead, refreshNotifications, showError, showSuccess, openQueueFor, registerNotificationsDrawer } = useNotifications();

	useEffect(() => {
		if (!open) return;
		registerNotificationsDrawer(onClose);
		return () => registerNotificationsDrawer(null);
	}, [open, onClose, registerNotificationsDrawer]);

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [filterType, setFilterType] = useState<"all" | "unread" | "read">("all");
	const [actionLoading, setActionLoading] = useState<string | null>(null);

	// Filtered notifications
	const filteredNotifications = useMemo(() => {
		let filtered = notifications;

		if (filterType === "unread") {
			filtered = filtered.filter((n) => !n.read_status);
		} else if (filterType === "read") {
			filtered = filtered.filter((n) => n.read_status);
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter((n) => n.title.toLowerCase().includes(query) || n.message.toLowerCase().includes(query));
		}

		return filtered;
	}, [notifications, filterType, searchQuery]);

	const allSelected = filteredNotifications.length > 0 && filteredNotifications.every((n) => selectedIds.has(n.id));
	const someSelected = selectedIds.size > 0;

	// Selection handlers
	const toggleSelect = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(filteredNotifications.map((n) => n.id)));
		}
	};

	const clearSelection = () => setSelectedIds(new Set());

	// Action handlers
	const handleMarkAsRead = async (id: string) => {
		try {
			await markAsRead(id);
		} catch {
			showError("Failed to mark notification as read.");
		}
	};

	const handleDelete = async (id: string) => {
		try {
			await deleteNotification(id);
			setSelectedIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			if (expandedId === id) setExpandedId(null);
		} catch {
			showError("Failed to delete notification.");
		}
	};

	const handleBulkDelete = async () => {
		if (!selectedIds.size) return;
		setActionLoading("bulk-delete");
		try {
			await bulkDelete(Array.from(selectedIds));
			showSuccess(`Deleted ${selectedIds.size} notification${selectedIds.size > 1 ? "s" : ""}.`);
			if (expandedId && selectedIds.has(expandedId)) setExpandedId(null);
			setSelectedIds(new Set());
		} catch {
			showError("Failed to delete selected notifications.");
		} finally {
			setActionLoading(null);
		}
	};

	const handleDeleteAll = async () => {
		setActionLoading("delete-all");
		try {
			await deleteAll();
			showSuccess("All notifications deleted.");
			setSelectedIds(new Set());
			setExpandedId(null);
		} catch {
			showError("Failed to delete all notifications.");
		} finally {
			setActionLoading(null);
		}
	};

	const handleMarkAllAsRead = async () => {
		setActionLoading("mark-all-read");
		try {
			await markAllAsRead();
			showSuccess("All notifications marked as read.");
		} catch {
			showError("Failed to mark all as read.");
		} finally {
			setActionLoading(null);
		}
	};

	const getNotificationIcon = (type: string) => {
		switch (type) {
			case "error":   return <CloseCircleOutlined style={{ color: "var(--mg-danger)", fontSize: 18 }} />;
			case "warning": return <ExclamationCircleOutlined style={{ color: "var(--mg-warning)", fontSize: 18 }} />;
			case "info":    return <InfoCircleOutlined style={{ color: "var(--mg-info)", fontSize: 18 }} />;
			default:        return <CheckCircleOutlined style={{ color: "var(--mg-success)", fontSize: 18 }} />;
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diff = now.getTime() - date.getTime();
		const minutes = Math.floor(diff / 60000);
		const hours = Math.floor(diff / 3600000);
		const days = Math.floor(diff / 86400000);

		if (minutes < 1) return "Just now";
		if (minutes < 60) return `${minutes}m ago`;
		if (hours < 24) return `${hours}h ago`;
		if (days < 7) return `${days}d ago`;
		return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
	};

	const formatFullDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-AU", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const renderNotificationCard = (notification: Notification) => {
		const isExpanded = expandedId === notification.id;
		const isSelected = selectedIds.has(notification.id);

		let borderColor = "var(--mg-border)";
		let bgColor = "transparent";
		if (isSelected) {
			borderColor = "var(--mg-primary)";
			bgColor = "var(--mg-primary-bg)";
		} else if (!notification.read_status || isExpanded) {
			borderColor = "var(--mg-primary-border)";
			bgColor = "var(--mg-primary-bg)";
		}

		return (
			<div
				key={notification.id}
				data-testid={`notifications-card-${notification.id}`}
				style={{
					padding: 12,
					borderRadius: 8,
					marginBottom: 8,
					border: "1px solid",
					borderColor,
					background: bgColor,
					opacity: notification.read_status && !isSelected ? 0.8 : 1,
					transition: "all 0.15s",
				}}
			>
				<div style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
					<Checkbox
						checked={isSelected}
						onChange={(e) => { e.stopPropagation(); toggleSelect(notification.id); }}
						onClick={(e) => e.stopPropagation()}
					/>
					<div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--mg-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
						{getNotificationIcon(notification.type)}
					</div>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
							<Text strong style={{ fontSize: 13 }} ellipsis>{notification.title}</Text>
							<Space size={4} style={{ flexShrink: 0 }}>
								{!notification.read_status && <ContrastTag background="var(--mg-primary)" style={{ fontSize: 11 }}>New</ContrastTag>}
								<Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{formatDate(notification.created_at)}</Text>
							</Space>
						</div>
						{!isExpanded && (
							<Text type="secondary" style={{ fontSize: 12, display: "block" }} ellipsis>{notification.message}</Text>
						)}
					</div>
					<Space size={8} style={{ flexShrink: 0 }}>
						{isQueueAlert(notification) && (
							<Button data-testid="notifications-open-queue-btn" size="small" type="primary" icon={<MessageOutlined />} onClick={() => openQueueFor(notification)}>
								Open queue
							</Button>
						)}
						{!notification.read_status && (
							<Button size="small" icon={<CheckOutlined />} onClick={() => handleMarkAsRead(notification.id)} />
						)}
						<Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(notification.id)} />
					</Space>
					<Button
						type="text"
						size="small"
						icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
						onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : notification.id); }}
						style={{ flexShrink: 0 }}
					/>
				</div>

				{isExpanded && (
					<div style={{ paddingTop: 12, borderTop: "1px solid var(--mg-border)", marginTop: 12 }}>
						<Text style={{ fontSize: 13, whiteSpace: "pre-wrap", display: "block", marginBottom: 4 }}>{notification.message}</Text>
						<Space size={16} wrap>
							<Text type="secondary" style={{ fontSize: 11 }}>{formatFullDate(notification.created_at)}</Text>
							{notification.related_entity_id && (
								<Text type="secondary" style={{ fontSize: 11 }}>Ref: {notification.related_entity_id}</Text>
							)}
						</Space>
					</div>
				)}
			</div>
		);
	};

	const notificationsTab = (
		<div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minHeight: 0 }}>
			{/* Toolbar */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
				<div style={{ display: "flex", gap: 12, flex: 1, flexWrap: "wrap" }}>
					<Input placeholder="Search notifications..." prefix={<SearchOutlined />} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear style={{ maxWidth: 280 }} />
				</div>
				<Space size={8}>
					{unreadCount > 0 && (
						<Button data-testid="notifications-mark-all-btn" size="small" icon={<CheckOutlined />} onClick={handleMarkAllAsRead} loading={actionLoading === "mark-all-read"}>
							Mark All Read
						</Button>
					)}
					{notifications.length > 0 && (
						<Tooltip title="Delete all notifications">
							<Button size="small" danger icon={<DeleteFilled />} onClick={handleDeleteAll} loading={actionLoading === "delete-all"}>
								Delete All
							</Button>
						</Tooltip>
					)}
				</Space>
			</div>

			{/* Selection Bar */}
			{filteredNotifications.length > 0 && (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: 12,
						borderRadius: 8,
						border: "1px solid",
						borderColor: someSelected ? "var(--mg-primary-border)" : "var(--mg-border)",
						background: someSelected ? "var(--mg-primary-bg)" : "var(--mg-bg-subtle)",
						transition: "all 0.15s",
					}}
				>
					<Checkbox checked={allSelected} indeterminate={someSelected && !allSelected} onChange={toggleSelectAll}>
						{someSelected ? `${selectedIds.size} selected` : "Select all"}
					</Checkbox>
					{someSelected && (
						<Space size={8}>
							<Button size="small" danger icon={<DeleteOutlined />} onClick={handleBulkDelete} loading={actionLoading === "bulk-delete"}>
								Delete Selected ({selectedIds.size})
							</Button>
							<Button size="small" type="text" onClick={clearSelection}>Clear</Button>
						</Space>
					)}
				</div>
			)}

			{/* Notification List */}
			<div style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
				{filteredNotifications.length === 0 ? (
					<div style={{ padding: "64px 0", textAlign: "center" }}>
						<Empty
							image={<BellOutlined style={{ fontSize: 48, color: "var(--mg-text-disabled)" }} />}
							description={<Text type="secondary">{searchQuery ? "No notifications match your search" : filterType !== "all" ? `No ${filterType} notifications` : "No notifications yet"}</Text>}
						/>
					</div>
				) : (
					filteredNotifications.map(renderNotificationCard)
				)}
			</div>
		</div>
	);

	return (
		<Drawer
			data-testid="notifications-drawer"
			open={open}
			onClose={onClose}
			placement="right"
			width={520}
			styles={{ body: { padding: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" } }}
			title={
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", paddingRight: 8 }}>
					<Space size={10}>
						<BellOutlined style={{ fontSize: 16 }} />
						<Title level={5} style={{ marginBottom: 0 }}>Notifications</Title>
					</Space>
					<Space size={8}>
						<Button data-testid="notifications-refresh-btn" type="text" size="small" icon={<ReloadOutlined />} loading={isLoading} onClick={refreshNotifications} />
					</Space>
				</div>
			}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16, flex: 1, minHeight: 0, overflow: "hidden" }}>
				{notificationsTab}
			</div>
		</Drawer>
	);
};


export default CommunicationsModal;
